import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSync } from "../context/SyncContext";
import { PageHeader, Confirm, EmptyState, useMeta } from "../components/Shared";
import { fmtFCFA, fmtDate, statusBadgeClass, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Eye, Pencil, Trash2, Link2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";
import PhoneField from "../components/PhoneField";
import { EditableListSelect } from "../components/EditableListSelect";
import { isValidEmail, isValidPhone } from "../lib/validate";

const empty = {
  name: "", owner: "", category: "", description: "", activities: [], total_cost: 0,
  start_date: "", end_date: "", status: "Pending", location: "", assigned_employees: [], notes: "", client_email: "", client_phone: "",
};

export default function Projects() {
  const { isRole } = useAuth();
  const meta = useMeta();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [activityInput, setActivityInput] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const canManage = isRole(ROLES.CEO);
  const canCreate = isRole(ROLES.CEO, ROLES.ACEO);

  const qc = useQueryClient();
  const { tick } = useSync();
  const { data: projects = [], isLoading: projectsLoading } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const { data: templates = [] } = useQuery({ queryKey: ["milestone-templates"], queryFn: () => api.get("/milestone-templates").then((r) => r.data) });
  const load = () => qc.invalidateQueries();
  const matchedTpl = templates.find((t) => t.category === form.category);

  const openNew = () => { setEditing(null); setEmailErr(""); setPhoneErr(""); setActivityInput(""); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setEmailErr(""); setPhoneErr(""); setActivityInput("");
    const activities = p.activities?.length ? p.activities : (p.description ? [p.description] : []);
    setForm({ ...empty, ...p, activities, start_date: (p.start_date || "").slice(0, 10), end_date: (p.end_date || "").slice(0, 10) });
    setOpen(true);
  };

  const addActivity = () => {
    const v = activityInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, activities: [...(f.activities || []), v] }));
    setActivityInput("");
  };
  const removeActivity = (i) => setForm((f) => ({ ...f, activities: (f.activities || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (form.client_email && !isValidEmail(form.client_email)) { setEmailErr("Enter a valid email address."); toast.error("Please fix the client email."); return; }
    if (form.client_phone && !isValidPhone(form.client_phone)) { setPhoneErr("Enter a valid phone number."); toast.error("Please fix the client phone."); return; }
    setEmailErr(""); setPhoneErr("");
    setSaving(true);
    try {
      const payload = { ...form, total_cost: Number(form.total_cost) || 0 };
      let resp = null;
      if (editing) { await api.put(`/projects/${editing.id}`, payload); }
      else { resp = (await api.post("/projects", payload)).data; }
      if (resp?.pending_approval) toast.info(resp.message || "Submitted to the CEO for approval");
      else toast.success(editing ? "Project updated" : "Project created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (p) => {
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleEmp = (id) => setForm((f) => ({
    ...f,
    assigned_employees: f.assigned_employees.includes(id)
      ? f.assigned_employees.filter((x) => x !== id)
      : [...f.assigned_employees, id],
  }));

  const copyPortal = (p) => {
    navigator.clipboard.writeText(`${window.location.origin}/portal/${p.public_token}`);
    toast.success("Client portal link copied to clipboard");
  };

  return (
    <div>
      <PageHeader title="Projects" subtitle="Create, track and manage every SilasTech project">
        <ExportMenu dataset="projects" filename="stems_projects" testId="projects-export" />
        {canCreate && (
          <Button data-testid="add-project-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-2" /> Add New Project
          </Button>
        )}
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {projectsLoading ? <TableSkeleton cols={8} /> : projects.length === 0 ? <EmptyState text="No projects yet." action={canCreate ? <Button data-testid="empty-new-project" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#152C6F]">Create your first project</Button> : null} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Contract</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id} data-testid={`project-row-${p.id}`} className="group">
                  <TableCell className="font-medium text-slate-800">{p.name}</TableCell>
                  <TableCell className="text-slate-600">{p.category}</TableCell>
                  <TableCell className="text-slate-600">{p.owner}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(p.total_cost)}</TableCell>
                  <TableCell className="text-right" data-testid={`project-received-${p.id}`}>
                    <div className="font-mono-num text-emerald-700">{fmtFCFA(p.financials?.amount_received)}</div>
                    <PaymentBar received={p.financials?.amount_received} contract={p.total_cost} />
                  </TableCell>
                  <TableCell className="text-right" data-testid={`project-balance-${p.id}`}>
                    {p.financials?.over_paid
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid={`overpaid-${p.id}`}><AlertTriangle className="h-3.5 w-3.5" /> Overpaid {fmtFCFA(p.financials?.overpaid_amount)}</span>
                      : p.financials?.payment_complete
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`payment-complete-${p.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Paid in full</span>
                        : <span className="font-mono-num">{fmtFCFA(p.financials?.contract_balance)}</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(p.status)}`}>{p.status}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <div data-testid={`project-actions-${p.id}`} className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white shadow-md px-1 py-0.5 opacity-100 transition-opacity md:focus-within:opacity-100">
                        <Button asChild size="icon" variant="ghost" data-testid={`view-project-${p.id}`} title="View">
                          <Link to={`/projects/${p.id}`}><Eye className="h-4 w-4" /></Link>
                        </Button>
                        {canManage && (
                          <>
                            <Button size="icon" variant="ghost" data-testid={`portal-project-${p.id}`} onClick={() => copyPortal(p)} title="Copy client portal link">
                              <Link2 className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" data-testid={`edit-project-${p.id}`} title="Edit" onClick={() => openEdit(p)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Confirm onConfirm={() => remove(p)} title="Delete project?"
                              description={`This permanently deletes "${p.name}". Financial records linked stay in the system.`}
                              trigger={<Button size="icon" variant="ghost" data-testid={`delete-project-${p.id}`} title="Delete" className="text-red-600"><Trash2 className="h-4 w-4" /></Button>} />
                          </>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Project" : "Add New Project"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Project Name"><Input data-testid="project-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Owner / Client"><Input data-testid="project-owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></Field>
            <Field label="Client Email (portal & milestone alerts)">
              <Input data-testid="project-client-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={emailErr ? "border-red-400" : ""} />
              {emailErr && <p className="text-xs text-red-600" data-testid="project-client-email-error">{emailErr}</p>}
            </Field>
            <Field label="Client Phone">
              <PhoneField value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} testId="project-client-phone" invalid={!!phoneErr} />
              {phoneErr && <p className="text-xs text-red-600" data-testid="project-client-phone-error">{phoneErr}</p>}
            </Field>
            <Field label="Category">
              <EditableListSelect value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}
                endpoint="/project-categories" noun="category" canManage={canManage}
                placeholder="Select category" testPrefix="project-category" refreshKey={tick} />
              {!editing && matchedTpl && <p className="text-xs text-emerald-700 mt-1" data-testid="tpl-match-hint">Timeline auto-applied: <b>{matchedTpl.name}</b> · {matchedTpl.stages.length} stages</p>}
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="project-status"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.project_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Total Contract Cost (FCFA)"><Input data-testid="project-cost" type="number" value={form.total_cost} onChange={(e) => setForm({ ...form, total_cost: e.target.value })} /></Field>
            <Field label="Location"><Input data-testid="project-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
            {editing && (
              <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="project-payment-summary">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Payment received (auto from income)</span>
                  <span className="font-semibold text-emerald-700 font-mono-num" data-testid="form-amount-received">{fmtFCFA(editing.financials?.amount_received)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1.5">
                  <span className="text-slate-500">Contract balance</span>
                  <span className="font-semibold font-mono-num" data-testid="form-contract-balance">{fmtFCFA(editing.financials?.contract_balance)}</span>
                </div>
                <PaymentBar received={editing.financials?.amount_received} contract={editing.total_cost} />
                {editing.financials?.over_paid ? (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="form-overpaid"><AlertTriangle className="h-3.5 w-3.5" /> Overpaid by {fmtFCFA(editing.financials?.overpaid_amount)}</div>
                ) : editing.financials?.payment_complete ? (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="form-payment-complete"><CheckCircle2 className="h-3.5 w-3.5" /> Payment complete</div>
                ) : null}
              </div>
            )}
            <Field label="Start Date"><Input data-testid="project-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Expected Completion"><Input data-testid="project-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <Label className="text-sm">Activities</Label>
              <div className="mt-1.5 flex gap-2">
                <Input data-testid="project-activity-input" value={activityInput}
                  onChange={(e) => setActivityInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addActivity(); } }}
                  placeholder="Add an activity, then click Add" />
                <Button type="button" variant="outline" data-testid="project-activity-add" onClick={addActivity}>Add</Button>
              </div>
              {(form.activities || []).length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="project-activities-list">
                  {form.activities.map((a, i) => (
                    <li key={i} data-testid={`project-activity-item-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
                      <span className="break-words">{a}</span>
                      <button type="button" data-testid={`project-activity-remove-${i}`} onClick={() => removeActivity(i)} className="shrink-0 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {employees.length > 0 && (
              <div className="sm:col-span-2">
                <Label className="text-sm">Assigned Employees</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {employees.map((e) => (
                    <button type="button" key={e.id} onClick={() => toggleEmp(e.id)}
                      className={`px-3 py-1.5 rounded-full text-xs border ${form.assigned_employees.includes(e.id) ? "bg-blue-600 text-white border-blue-600" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {e.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <Field label="Remarks"><Textarea data-testid="project-remarks" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="project-form-submit" onClick={save} disabled={saving || !form.name} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">
              {editing ? "Save Changes" : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function PaymentBar({ received, contract }) {
  const r = Number(received || 0), c = Number(contract || 0);
  if (c <= 0) return null;
  const pct = Math.min(100, Math.round((r / c) * 100));
  const over = r > c;
  return (
    <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden" title={`${pct}% paid`} data-testid="payment-bar">
      <div className={`h-full ${over ? "bg-red-500" : pct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
    </div>
  );
}
